import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Heart, X, Copy, Check, LogIn, BookmarkPlus, Palette, Layers, Shuffle, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useAppContext } from '../contexts/AppContext';
import { checkIfHearted, heartPalette } from '../services/galleryService';
import { decodePaletteFromShare } from '../utils/paletteCode';
import { getMarkerCode } from '../utils/color';
import { savePalette } from '../config/savedPalettes';
import { getUserCollection } from '../utils/userCollection';
import { convertMarkersToCollection } from '../utils/colorConvert';
import { parseMoods } from '../utils/moods';
import { MoodChips } from './MoodPicker';

// ─── Heart delta helpers (mirrors GalleryPage) ────────────────────────────────

function getHeartDelta(id) {
  return parseInt(localStorage.getItem(`heartDelta_${id}`) || '0', 10);
}
function setHeartDelta(id, delta) {
  if (delta === 0) localStorage.removeItem(`heartDelta_${id}`);
  else localStorage.setItem(`heartDelta_${id}`, String(delta));
}
function reconcileHeartBase(id, currentBase) {
  const storedBase = parseInt(localStorage.getItem(`heartBase_${id}`) || String(currentBase), 10);
  const delta = getHeartDelta(id);
  if (currentBase !== storedBase) {
    localStorage.setItem(`heartBase_${id}`, String(currentBase));
    return 0;
  }
  return delta;
}

// ─── Save sub-modal ───────────────────────────────────────────────────────────

function SavePaletteModal({ palette, markers, user, onClose, onSaved }) {
  const [saveMode, setSaveMode] = useState(null); // 'asis' | 'convert'
  const [paletteName, setPaletteName] = useState(palette?.name || '');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);

  const collectionMarkers = convertMarkersToCollection(markers);
  const noCollection = getUserCollection().length === 0;

  useEffect(() => {
    const handle = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [onClose]);

  const handleSave = async () => {
    if (!paletteName.trim()) { setSaveError('Please enter a name.'); return; }
    setSaving(true);
    setSaveError('');
    const markersToSave = saveMode === 'convert' ? collectionMarkers : markers;
    const result = await savePalette(user.uid, markersToSave, paletteName.trim());
    setSaving(false);
    if (result.success) {
      setSaveSuccess(true);
      setTimeout(onSaved, 1200);
    } else {
      setSaveError(result.error || 'Failed to save palette.');
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[70] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeOnly"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative z-10 bg-white rounded-2xl max-w-sm w-full shadow-2xl overflow-hidden animate-slideUp">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4">
          <div>
            <div className="flex items-center gap-2">
              <BookmarkPlus size={20} className="text-emerald-500" />
              <h3 className="text-lg font-bold text-slate-900">Save Palette</h3>
            </div>
            <div className="w-16 h-1 bg-gradient-to-r from-emerald-400 to-teal-400 rounded-full mt-2" />
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="px-6 pb-6">
          {saveSuccess ? (
            <div className="py-6 text-center">
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                <Check size={24} className="text-emerald-600" />
              </div>
              <p className="text-sm font-medium text-slate-800">Saved!</p>
            </div>
          ) : (
            <>
              {saveError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                  <AlertCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{saveError}</p>
                </div>
              )}

              {/* Name input (shown when a save mode is selected) */}
              {saveMode && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Palette name</label>
                  <div className="flex gap-1 items-center">
                    <input
                      type="text"
                      value={paletteName}
                      onChange={(e) => { setPaletteName(e.target.value); setSaveError(''); }}
                      placeholder="e.g. Autumn Vibes"
                      maxLength={50}
                      autoFocus
                      className="flex-1 px-3 py-1.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm transition-all"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !saving && paletteName.trim()) handleSave();
                        if (e.key === 'Escape') { setSaveMode(null); setSaveError(''); }
                      }}
                      disabled={saving}
                    />
                    <button
                      onClick={handleSave}
                      disabled={saving || !paletteName.trim()}
                      className="p-1.5 text-emerald-600 hover:text-emerald-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      title="Save"
                    >
                      <Check size={18} />
                    </button>
                    <button
                      onClick={() => { setSaveMode(null); setSaveError(''); }}
                      disabled={saving}
                      className="p-1.5 text-slate-400 hover:text-slate-600 transition-colors"
                      title="Cancel"
                    >
                      <X size={18} />
                    </button>
                  </div>
                </div>
              )}

              <div className="space-y-3">
                {/* Save as-is */}
                <button
                  onClick={() => { setSaveMode('asis'); setSaveError(''); }}
                  disabled={saving || saveMode === 'asis'}
                  className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all group text-left disabled:cursor-not-allowed ${
                    saveMode === 'asis'
                      ? 'border-emerald-300 bg-emerald-50'
                      : 'border-slate-100 hover:border-emerald-300 hover:bg-emerald-50 disabled:opacity-50'
                  }`}
                >
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <Layers size={20} className="text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-800 group-hover:text-emerald-700 transition-colors">Save as-is</p>
                    <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                      Save all {markers.length} original markers exactly as they appear.
                    </p>
                  </div>
                </button>

                {/* Convert & Save */}
                <button
                  onClick={() => { setSaveMode('convert'); setSaveError(''); }}
                  disabled={saving || noCollection || collectionMarkers.length === 0 || saveMode === 'convert'}
                  className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all group text-left disabled:cursor-not-allowed ${
                    saveMode === 'convert'
                      ? 'border-purple-300 bg-purple-50'
                      : 'border-slate-100 hover:border-purple-300 hover:bg-purple-50 disabled:opacity-50'
                  }`}
                >
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-400 to-violet-500 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <Shuffle size={20} className="text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-slate-800 group-hover:text-purple-700 transition-colors">Convert &amp; Save</p>
                      {!noCollection && collectionMarkers.length > 0 && (
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">
                          {collectionMarkers.length} markers
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                      {noCollection
                        ? 'Add markers to your collection first.'
                        : collectionMarkers.length === 0
                        ? 'No markers in your collection match this palette.'
                        : `Each marker matched to the closest one you own. You may get fewer than ${markers.length} markers.`
                      }
                    </p>
                  </div>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── Main modal ───────────────────────────────────────────────────────────────

export default function ProfilePaletteModal({ palette, isOwner, onClose }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { ohuhuMode } = useAppContext();

  const [hearted, setHearted] = useState(false);
  const [heartCount, setHeartCount] = useState(palette?.heartCount || 0);
  const [heartLoading, setHeartLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [markers, setMarkers] = useState([]);
  const [saveModalOpen, setSaveModalOpen] = useState(false);

  // Decode markers
  useEffect(() => {
    if (!palette?.code) return;
    try {
      setMarkers(decodePaletteFromShare(palette.code) || []);
    } catch {
      setMarkers([]);
    }
  }, [palette]);

  // Heart state + delta reconciliation
  useEffect(() => {
    if (!palette?.id) return;
    const base = palette.heartCount || 0;
    const delta = reconcileHeartBase(palette.id, base);
    setHeartCount(Math.max(0, base + delta));
    if (user) checkIfHearted(user.uid, palette.id).then(setHearted);
  }, [user, palette]);

  // Escape + scroll lock
  useEffect(() => {
    document.documentElement.style.overflow = 'hidden';
    return () => { document.documentElement.style.overflow = ''; };
  }, []);
  useEffect(() => {
    const handle = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [onClose]);

  const handleHeartToggle = async () => {
    if (!user) { navigate('/palettes'); onClose(); return; }
    if (heartLoading) return;
    setHeartLoading(true);
    const wasHearted = hearted;
    setHearted(!wasHearted);
    setHeartCount(c => wasHearted ? c - 1 : c + 1);
    const result = await heartPalette(user.uid, palette.id);
    if (!result.success) {
      setHearted(wasHearted);
      setHeartCount(c => wasHearted ? c + 1 : c - 1);
    } else {
      const base = palette.heartCount || 0;
      const prevDelta = getHeartDelta(palette.id);
      const newDelta = wasHearted
        ? (prevDelta > 0 ? 0 : -1)
        : (prevDelta < 0 ? 0 : 1);
      setHeartDelta(palette.id, newDelta);
      setHeartCount(Math.max(0, base + newDelta));
    }
    setHeartLoading(false);
  };

  const handleCopyCode = () => {
    if (!palette?.code) return;
    navigator.clipboard.writeText(palette.code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const getCode = (marker) => getMarkerCode(marker, ohuhuMode);

  if (!palette) return null;

  const allColors = markers.length > 0 ? markers.map(m => m.hex) : (palette.previewColors || []);

  return createPortal(
    <div
      className="fixed inset-0 z-[60] backdrop-blur-sm bg-black/30 flex items-center justify-center p-4 animate-fadeOnly"
      onClick={(e) => { if (e.target === e.currentTarget && !saveModalOpen) onClose(); }}
    >
      <div className="bg-white rounded-3xl max-w-lg w-full shadow-2xl border border-slate-100 flex flex-col max-h-[90vh] animate-slideUp">

        {/* Color strip */}
        <div className="flex h-28 rounded-t-3xl overflow-hidden flex-shrink-0">
          {allColors.length > 0
            ? allColors.map((hex, i) => <div key={i} className="flex-1" style={{ backgroundColor: hex }} />)
            : <div className="flex-1 bg-slate-100" />
          }
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-bold text-slate-900 leading-tight truncate">{palette.name}</h2>
              <p className="text-sm text-slate-500 mt-0.5 truncate">
                by {palette.authorName || 'Unknown'}{palette.createdAt && <> &middot; {new Date(palette.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</>}
              </p>
            </div>
            <button onClick={onClose} className="p-2 rounded-xl hover:bg-slate-100 transition-colors flex-shrink-0">
              <X size={18} className="text-slate-500" />
            </button>
          </div>

          {/* Swatch grid */}
          {markers.length > 0 && (
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 mb-4">
              {markers.map((m, i) => {
                const hex = m.hex || '#E5E7EB';
                const cleanHex = hex.replace('#', '');
                const r = parseInt(cleanHex.substr(0, 2), 16);
                const g = parseInt(cleanHex.substr(2, 2), 16);
                const b = parseInt(cleanHex.substr(4, 2), 16);
                const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
                const textColor = luminance > 0.5 ? '#1e293b' : '#ffffff';
                const rawCode = getCode(m);
                const codeSegments = rawCode.split('/').map(s => s.trim()).filter(Boolean);
                return (
                  <div key={i} className="flex flex-col">
                    <div className="w-full aspect-square rounded-xl border-4 border-white shadow-inner relative" style={{ backgroundColor: hex }}>
                      <div className="absolute bottom-2 left-2 flex flex-wrap items-center gap-1">
                        {codeSegments.map((seg, idx) => (
                          <span key={idx} className="px-0.5 text-[10px] font-mono font-semibold uppercase tracking-[0.3em]" style={{ color: textColor }}>
                            {seg}
                          </span>
                        ))}
                      </div>
                    </div>
                    <p className="text-[8px] font-semibold uppercase text-slate-500 truncate mt-1.5 px-2">{m.brand}</p>
                  </div>
                );
              })}
            </div>
          )}

          {/* Code box */}
          {palette.code && (
            <div className="bg-slate-50 rounded-xl p-3 flex items-center gap-2">
              <p className="text-xs font-mono text-slate-600 flex-1 break-all leading-relaxed">{palette.code}</p>
              <button onClick={handleCopyCode} className="p-2 rounded-lg hover:bg-slate-200 transition-colors flex-shrink-0">
                {copied ? <Check size={15} className="text-green-600" /> : <Copy size={15} className="text-slate-500" />}
              </button>
            </div>
          )}

          {/* Moods */}
          {palette.moods && (
            <div className="mt-3">
              <MoodChips moodNames={parseMoods(palette.moods)} />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center gap-3 p-5 pt-0 flex-shrink-0">
          {isOwner ? (
            /* Owner: Go to My Palettes */
            <button
              onClick={() => { navigate('/palettes'); onClose(); }}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium bg-slate-50 border border-slate-200 text-slate-600 hover:bg-slate-100 hover:border-slate-300 transition-all"
            >
              <Palette size={15} />
              Go to My Palettes
            </button>
          ) : (
            /* Public: Heart + Save */
            <>
              <button
                onClick={handleHeartToggle}
                disabled={heartLoading}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-all border ${
                  hearted
                    ? 'bg-red-50 border-red-200 text-red-600'
                    : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-red-50 hover:border-red-200 hover:text-red-600'
                }`}
              >
                {user
                  ? <Heart size={15} className={hearted ? 'fill-red-500 text-red-500' : ''} />
                  : <LogIn size={15} />
                }
                {(!palette.hideLikes || !user) && <span>{user ? heartCount : 'Sign in'}</span>}
              </button>

              <button
                onClick={() => {
                  if (!user) { navigate('/palettes'); onClose(); return; }
                  setSaveModalOpen(true);
                }}
                disabled={markers.length === 0}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100 hover:border-emerald-300 transition-all disabled:opacity-40"
              >
                <BookmarkPlus size={15} />
                Save
              </button>
            </>
          )}
        </div>
      </div>

      {saveModalOpen && (
        <SavePaletteModal
          palette={palette}
          markers={markers}
          user={user}
          onClose={() => setSaveModalOpen(false)}
          onSaved={() => { setSaveModalOpen(false); onClose(); }}
        />
      )}
    </div>,
    document.body
  );
}
